import {
  Autocomplete,
  Box,
  Checkbox,
  Dialog,
  IconButton,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { ReactComponent as CloseIcon } from "../../assets/images/close.svg";
import CustomButton from "../../components/CustomButton";
import CustomTextFieldPopup from "../../components/CustomTextFieldPopup";
import CommonUtil from "../../util/CommonUtils";
import AppContext from "../../config/AppContext";
import { countryList } from "../CountryList";
import {
  getCustomerDetails,
  updateCustomerDetails,
} from "../../services/Subscription";
import { sidebarContext } from "../../util/context";
import "./BillingAddressForm.scss";
import { useTranslation } from "react-i18next";
import CustomTextField from "../CustomTextField";

export default function BillingAddressForm(props) {
  const { mode } = useContext(AppContext);
  const { t, i18n } = useTranslation();
  const { subscriptionDetails } = useContext(sidebarContext);

  const customerId = subscriptionDetails?.user?.customerId || "";

  const [error, setError] = useState({
    billing: {
      attention: "",
      street: "",
      city: "",
      state: "",
      zip: "",
      country: "",
    },
    shipping: {
      attention: "",
      street: "",
      city: "",
      state: "",
      zip: "",
      country: "",
    },
  });

  const [payload, setPayload] = useState({
    display_name: "",
    first_name: "",
    email: "",
    company_name: "",
    currency_code: "",
    billing_address: {
      attention: "",
      street: "",
      city: "",
      state: "",
      zip: "",
      country: "",
      state_code: "",
      fax: "",
    },
    shipping_address: {
      attention: "",
      street: "",
      city: "",
      state: "",
      zip: "",
      country: "",
      state_code: "",
      fax: "",
    },
    sameAsBilling: true,
  });

  useEffect(() => {
    if (props.open && customerId) {
      const fetchCustomerDetails = async () => {
        try {
          const response = await getCustomerDetails(customerId);
          const customerData = response?.data?.customer;
          setPayload({
            display_name: customerData.display_name || "",
            first_name: customerData.first_name || "",
            email: customerData.email || "",
            company_name: customerData.company_name || "",
            currency_code: customerData.currency_code || "",
            billing_address: {
              attention: customerData.billing_address?.attention || "",
              street: customerData.billing_address?.street || "",
              city: customerData.billing_address?.city || "",
              state: customerData.billing_address?.state || "",
              zip: customerData.billing_address?.zip || "",
              country: customerData.billing_address?.country || "",
              state_code: customerData.billing_address?.state_code || "",
              fax: customerData.billing_address?.fax || "",
            },
            shipping_address: {
              attention: customerData.shipping_address?.attention || "",
              street: customerData.shipping_address?.street || "",
              city: customerData.shipping_address?.city || "",
              state: customerData.shipping_address?.state || "",
              zip: customerData.shipping_address?.zip || "",
              country: customerData.shipping_address?.country || "",
              state_code: customerData.shipping_address?.state_code || "",
              fax: customerData.shipping_address?.fax || "",
            },
            sameAsBilling: true,
          });
          setError({
            billing: {
              attention: "",
              street: "",
              city: "",
              state: "",
              zip: "",
              country: "",
            },
            shipping: {
              attention: "",
              street: "",
              city: "",
              state: "",
              zip: "",
              country: "",
            },
          });
        } catch (error) {
          console.error("Error fetching customer details:", error);
          props.onClose();
        }
      };
      fetchCustomerDetails();
    }
  }, [props.open, customerId]);

  const handleBillingFieldChange = (field) => (e) => {
    const value = e.target.value;

    setPayload((prev) => ({
      ...prev,
      billing_address: {
        ...prev.billing_address,
        [field]: value,
      },
      shipping_address: prev.sameAsBilling
        ? {
            ...prev.shipping_address,
            [field]: value,
          }
        : prev.shipping_address,
    }));

    setError((prev) => ({
      ...prev,
      billing: {
        ...prev.billing,
        [field]: "",
      },
      shipping: prev.sameAsBilling
        ? {
            ...prev.shipping,
            [field]: "",
          }
        : prev.shipping,
    }));
  };

  const handleBillingCountryChange = (value) => {
    const countryValue = value?.country || "";

    setPayload((prev) => ({
      ...prev,
      billing_address: {
        ...prev.billing_address,
        country: countryValue,
      },
      shipping_address: prev.sameAsBilling
        ? {
            ...prev.shipping_address,
            country: countryValue,
          }
        : prev.shipping_address,
    }));

    setError((prev) => ({
      ...prev,
      billing: {
        ...prev.billing,
        country: "",
      },
      shipping: prev.sameAsBilling
        ? {
            ...prev.shipping,
            country: "",
          }
        : prev.shipping,
    }));
  };

  const handleShippingFieldChange = (field) => (e) => {
    const value = e.target.value;

    setPayload((prev) => ({
      ...prev,
      shipping_address: {
        ...prev.shipping_address,
        [field]: value,
      },
    }));

    setError((prev) => ({
      ...prev,
      shipping: {
        ...prev.shipping,
        [field]: "",
      },
    }));
  };

  const handleShippingCountryChange = (value) => {
    const countryValue = value?.country || "";

    setPayload((prev) => ({
      ...prev,
      shipping_address: {
        ...prev.shipping_address,
        country: countryValue,
      },
    }));

    setError((prev) => ({
      ...prev,
      shipping: {
        ...prev.shipping,
        country: "",
      },
    }));
  };

  const handleCheckboxChange = (e) => {
    const checked = e.target.checked;

    setPayload((prev) => ({
      ...prev,
      sameAsBilling: checked,
      shipping_address: checked
        ? { ...prev.billing_address }
        : prev.shipping_address,
    }));

    if (checked) {
      setError((prev) => ({
        ...prev,
        shipping: {
          attention: "",
          street: "",
          city: "",
          state: "",
          zip: "",
          country: "",
        },
      }));
    }
  };

  const validateFields = () => {
    let isValid = true;
    const newErrors = {
      billing: { ...error.billing },
      shipping: { ...error.shipping },
    };

    // Validate billing address
    if (!payload.billing_address.attention) {
      newErrors.billing.attention = t("RAL0001");
      isValid = false;
    }
    if (!payload.billing_address.country) {
      newErrors.billing.country = t("RAL0001");
      isValid = false;
    }
    if (!payload.billing_address.state) {
      newErrors.billing.state = t("RAL0001");
      isValid = false;
    }
    if (!payload.billing_address.zip) {
      newErrors.billing.zip = t("RAL0001");
      isValid = false;
    }
    // else if (
    //   !CommonUtil.isValidPasitiveNumeric(payload.billing_address.zip)
    // ) {
    //   newErrors.billing.zip = t("RAL0444");
    //   isValid = false;
    // }

    if (!payload.sameAsBilling) {
      if (!payload.shipping_address.attention) {
        newErrors.shipping.attention = t("RAL0001");
        isValid = false;
      }
      if (!payload.shipping_address.country) {
        newErrors.shipping.country = t("RAL0001");
        isValid = false;
      }
      if (!payload.shipping_address.state) {
        newErrors.shipping.state = t("RAL0001");
        isValid = false;
      }
      if (!payload.shipping_address.zip) {
        newErrors.shipping.zip = t("RAL0001");
        isValid = false;
      }
      // else if (
      //   !CommonUtil.isValidPasitiveNumeric(payload.shipping_address.zip)
      // ) {
      //   newErrors.shipping.zip = t("RAL0444");
      //   isValid = false;
      // }
    }

    setError(newErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateFields()) return;

    try {
      const submitPayload = {
        display_name: payload.display_name,
        first_name: payload.first_name,
        email: payload.email,
        company_name: payload.company_name,
        currency_code: payload.currency_code,
        billing_address: payload.billing_address,
        shipping_address: payload.sameAsBilling
          ? payload.billing_address
          : payload.shipping_address,
      };

      const res = await updateCustomerDetails(customerId, submitPayload);
      if (res?.data?.code !== "DP0000") {
        toast.success(t("RAL1156"));
        props.onSubmit();
        props.onClose();
      } else {
        // props.onSubmit();
        props.onClose();
        console.error("Error updating customer details:", res?.data?.message);
        toast.error("Error updating customer details");
      }
    } catch (error) {
      console.error("Error updating customer details:", error);
    }
  };

  const handleClose = () => {
    props.onClose();
  };

  const handleSkip = () => {
    if (validateFields()) {
      props.onSubmit();
    }
  };

  const getSelectedCountry = (countryName) => {
    return (
      countryList.find((country) => country.country === countryName) || null
    );
  };

  return (
    <div className="billing-address-container">
      <Dialog open={props.open} className="billing-address-dialog">
        <div
          className={
            mode === "dark"
              ? "billing-address-contentdark"
              : "billing-address-content"
          }
        >
          <Box className="billing-address-header">
            <Box item flexGrow={1}>
              <Typography
                className={
                  mode === "dark"
                    ? "billing-title-text-dark"
                    : "billing-title-text"
                }
              >
                {t("RAL1147")}
              </Typography>
            </Box>

            <IconButton
              onClick={handleClose}
              sx={{
                border:
                  mode === "light" ? "1px solid #FFFFFF" : "1px solid #313131",
                borderRadius: "5px",
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <div className="billing-address-container12">
            <Typography
              className={
                mode === "dark"
                  ? "billing-sub-title-text-dark"
                  : "billing-sub-title-text"
              }
            >
              {t("RAL1148")}
            </Typography>

            {/* Billing Address Fields */}
            <div className="address-row-billing">
              <Box className="attention-field">
                <CustomTextFieldPopup
                  id="attention-billing"
                  name="attention"
                  label={t("RAL1150")}
                  required
                  error={!!error.billing.attention}
                  helperText={error.billing.attention}
                  value={payload.billing_address.attention}
                  onChange={handleBillingFieldChange("attention")}
                  inputProps={{ maxLength: 100 }}
                />
              </Box>

              <Box className="country-field">
                <Autocomplete
                  id="billing-country"
                  fullWidth
                  size="small"
                  getOptionLabel={(option) => option.country || ""}
                  value={getSelectedCountry(payload.billing_address.country)}
                  onChange={(_, newValue) =>
                    handleBillingCountryChange(newValue)
                  }
                  options={countryList}
                  noOptionsText={t("RAL0961")}
                  renderInput={(params) => (
                    <CustomTextFieldPopup
                      {...params}
                      dropdown={true}
                      label={t("RAL0141")}
                      required
                      error={!!error.billing.country}
                      helperText={error.billing.country}
                    />
                  )}
                />
              </Box>
            </div>

            <div className="address-row-state">
              <Box className="state-field">
                <CustomTextFieldPopup
                  id="state-billing"
                  name="state"
                  label={t("RAL1151")}
                  required
                  error={!!error.billing.state}
                  helperText={error.billing.state}
                  value={payload.billing_address.state}
                  onChange={handleBillingFieldChange("state")}
                  inputProps={{ maxLength: 100 }}
                />
              </Box>
              <Box className="city-field">
                <CustomTextFieldPopup
                  id="city-billing"
                  name="city"
                  label={t("RAL1152")}
                  error={!!error.billing.city}
                  helperText={error.billing.city}
                  value={payload.billing_address.city}
                  onChange={handleBillingFieldChange("city")}
                  inputProps={{ maxLength: 100 }}
                />
              </Box>
            </div>

            <div className="address-row-state">
              <Box className="zip-field">
                <CustomTextFieldPopup
                  id="zip-billing"
                  name="zip"
                  label={t("RAL1153")}
                  required
                  error={!!error.billing.zip}
                  helperText={error.billing.zip}
                  value={payload.billing_address.zip}
                  onChange={handleBillingFieldChange("zip")}
                  inputProps={{
                    maxLength: 20,
                    pattern: "[0-9]*",
                    inputMode: "numeric",
                  }}
                />
              </Box>
              <Box className="street-field">
                <CustomTextFieldPopup
                  id="street-billing"
                  name="street"
                  label={t("RAL1154")}
                  error={!!error.billing.street}
                  helperText={error.billing.street}
                  value={payload.billing_address.street}
                  onChange={handleBillingFieldChange("street")}
                  inputProps={{ maxLength: 200 }}
                />
              </Box>
            </div>

            {/* Same as Billing Checkbox */}
            <div className="checkbox-row-billing">
              <Box className="checkbox-container-billing">
                <Checkbox
                  id="sameAsBilling"
                  checked={payload.sameAsBilling}
                  onChange={handleCheckboxChange}
                  sx={{
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                    "&.Mui-checked:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                />
                <span
                  className={
                    mode === "dark"
                      ? "billing-details-dark"
                      : "billing-details-text"
                  }
                >
                  {t("RAL1155")}
                </span>
              </Box>
              <button className="skip-button-billing" onClick={handleSkip}>
                <span
                  className={mode === "dark" ? "skip-text-dark" : "skip-text"}
                >
                  {t("RAL0144")}
                </span>
              </button>
            </div>

            {/* Shipping Address Fields (when not same as billing) */}
            {!payload.sameAsBilling && (
              <>
                <Typography
                  className={
                    mode === "dark"
                      ? "billing-sub-title-text-dark"
                      : "billing-sub-title-text"
                  }
                >
                  {t("RAL1149")}
                </Typography>

                <div className="address-row-billing">
                  <Box className="attention-field">
                    <CustomTextFieldPopup
                      id="attention-shipping"
                      name="attention"
                      label={t("RAL1150")}
                      required
                      error={!!error.shipping.attention}
                      helperText={error.shipping.attention}
                      value={payload.shipping_address.attention}
                      onChange={handleShippingFieldChange("attention")}
                      inputProps={{ maxLength: 100 }}
                    />
                  </Box>

                  <Box className="country-field">
                    <Autocomplete
                      id="shipping-country"
                      fullWidth
                      size="small"
                      getOptionLabel={(option) => option.country || ""}
                      value={getSelectedCountry(
                        payload.shipping_address.country
                      )}
                      onChange={(_, newValue) =>
                        handleShippingCountryChange(newValue)
                      }
                      options={countryList}
                      noOptionsText={t("RAL0961")}
                      renderInput={(params) => (
                        <CustomTextFieldPopup
                          {...params}
                          dropdown={true}
                          label={t("RAL0141")}
                          required
                          error={!!error.shipping.country}
                          helperText={error.shipping.country}
                        />
                      )}
                    />
                  </Box>
                </div>

                <div className="address-row-state">
                  <Box className="state-field">
                    <CustomTextFieldPopup
                      id="state-shipping"
                      name="state"
                      label={t("RAL1151")}
                      required
                      error={!!error.shipping.state}
                      helperText={error.shipping.state}
                      value={payload.shipping_address.state}
                      onChange={handleShippingFieldChange("state")}
                      inputProps={{ maxLength: 100 }}
                    />
                  </Box>
                  <Box className="city-field">
                    <CustomTextFieldPopup
                      id="city-shipping"
                      name="city"
                      label={t("RAL1152")}
                      error={!!error.shipping.city}
                      helperText={error.shipping.city}
                      value={payload.shipping_address.city}
                      onChange={handleShippingFieldChange("city")}
                      inputProps={{ maxLength: 100 }}
                    />
                  </Box>
                </div>

                <div className="address-row-state">
                  <Box className="zip-field">
                    <CustomTextFieldPopup
                      id="zip-shipping"
                      name="zip"
                      label={t("RAL1153")}
                      required
                      error={!!error.shipping.zip}
                      helperText={error.shipping.zip}
                      value={payload.shipping_address.zip}
                      onChange={handleShippingFieldChange("zip")}
                      inputProps={{
                        maxLength: 20,
                        pattern: "[0-9]*",
                        inputMode: "numeric",
                      }}
                    />
                  </Box>
                  <Box className="street-field">
                    <CustomTextFieldPopup
                      id="street-shipping"
                      name="street"
                      label={t("RAL1154")}
                      error={!!error.shipping.street}
                      helperText={error.shipping.street}
                      value={payload.shipping_address.street}
                      onChange={handleShippingFieldChange("street")}
                      inputProps={{ maxLength: 200 }}
                    />
                  </Box>
                </div>
              </>
            )}
          </div>

          <div className="popup-footer1">
            <button
              className={
                mode === "dark"
                  ? "popup-footercancelDark1"
                  : "popup-footercancel1"
              }
              onClick={handleClose}
            >
              {t("RAL0177")}
            </button>
            <button className="popup-footerconfirm1" onClick={handleSubmit}>
              {t("RAL0309")}
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
